/**
 * The walkaround's plot mesh: built across frames once the SURFACE edge is
 * crossed, revealed with its scatter on one frame, and given back on a body
 * change or a dispose; with the frame slices and the program warm queue the
 * build yields through.
 */

import * as THREE from 'three';

import type { AtlasLive, AtlasState, Site } from '../atlas';
import { track } from '../visits/bus.ts';
import { urlFlag, TEST_SWITCHES } from '../urlFlags.ts';
import { DtmPlot, plotLatticeSpec, uploadTextureStaged } from './dtmPlot.ts';
import type { LatticeBuffers } from './plotLattice';
import { plotClimate, softClimate, type SubPlot } from './plotField.ts';
import { isPyramidPlot } from './pyramidPlot.ts';
import { loadBitmap } from './imageBitmap.ts';
import type { PyramidViewHandle } from './pyramidView.ts';
import type { CascadesHandle } from './cascades.ts';
import type { GlobeMaterialHandle } from './globeMaterial.ts';
import type { GroundColourHandle, PlotGround } from './groundColour.ts';
import type { DescentLoadHandle } from './descentLoad.ts';
import type { CameraRigHandle } from './cameraRig.ts';
import type { FarFieldHandle } from './farField.ts';
import type { InspectorHandle } from './inspector.ts';
import type { FrameGovernorHandle } from './frameGovernor.ts';
import type { MountHandle } from './mount.ts';
import type { AtmosphereViewHandle } from './atmosphereView.ts';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { PlotMountHandle } from './plotMount.ts';
import type { SelectionHandle } from './selection.ts';
import type { PlotSunHandle } from './plotSun.ts';
import type { SolarScrubHandle } from './solarScrub.ts';
import type { InjectsHandle } from './injects.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import type { RockTuningHandle } from './panels/rockTuning.ts';
import type { RoverDriveHandle } from './roverDrive.ts';
import type { SiteGroundHandle } from './siteGround.ts';
import type { DescentAnchorHandle } from './descentAnchor.ts';
import type { PlotGeometryHandle } from './plotGeometry.ts';
import type { OverlayViewHandle } from './overlayView.ts';
import type { PlotControlsHandle } from './panels/plotControls.ts';

export interface PlotBuildDeps {
  site(): Site | undefined;
  readonly root: HTMLElement;
  readonly state: AtlasState;
  readonly renderer: THREE.WebGLRenderer;
  readonly frameGovernor: FrameGovernorHandle;
  readonly mount: MountHandle;
  readonly atmosphereView: AtmosphereViewHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly solarScrub: SolarScrubHandle;
  readonly pyramidView: PyramidViewHandle;
  readonly cascades: CascadesHandle;
  readonly globeMaterial: GlobeMaterialHandle;
  readonly groundColour: GroundColourHandle;
  readonly descentLoad: DescentLoadHandle;
  readonly plotMount: PlotMountHandle;
  readonly selection: SelectionHandle;
  readonly plotSun: PlotSunHandle;
  readonly injects: InjectsHandle;
  readonly bodyScene: BodySceneHandle;
  /** Written from inside this seam. */
  readonly live: AtlasLive;
  readonly rockTuningPanel: RockTuningHandle;
  readonly roverDrive: RoverDriveHandle;
  readonly siteGround: SiteGroundHandle;
  readonly descentAnchorHandle: DescentAnchorHandle;
  readonly plotGeometry: PlotGeometryHandle;
  readonly overlayView: OverlayViewHandle;
  readonly plotControls: PlotControlsHandle;
  readonly cameraRig: CameraRigHandle;
  readonly farField: FarFieldHandle;
  readonly inspector: InspectorHandle;
}

/** The build's own state that the mount, the tick and the probe read or write. */
export interface PlotBuildState {
  warmQueue: Array<{ obj: THREE.Object3D; done: () => void }>;
  readonly plotBuilding: DtmPlot | null;
  plotGround: PlotGround;
  plotBuildMainThread: boolean;
  readonly plotRevealedWithRocks: boolean;
  buildBudgetOff: boolean;
  surveyRecompute: boolean;
  readonly plotBuildSpans: {
    measureMs: number; rocksMs: number; surveyWorkMs: number; scoreWorkMs: number;
    surveyedHere: number; handedSurvey: boolean; orthoMean: number | null;
    stepWorstMs: number; stepWorstName: string; stepFrames: number; steps: [string, number][];
  };
}

export interface PlotBuildHandle {
  warmPrograms(obj: THREE.Object3D, deadlineMs?: number): Promise<void>;
  frameSlices(budgetMs?: number): () => Promise<void>;
  ensurePlotMesh(): Promise<void>;
  releasePlot(): void;
  readonly plotEarlyReveal: boolean;
  readonly state: PlotBuildState;
}

export function createPlotBuild(deps: PlotBuildDeps): PlotBuildHandle {
  const { site, root, state, renderer, pyramidView, cascades, globeMaterial, descentLoad } = deps;
  const { markLoad, loadStarted, loadFinished } = deps.frameGovernor;
  const { camera } = deps.mount;
  const { plotAtmoRef } = deps.atmosphereView;
  const { rowById } = deps.bodyReaders;
  const { solar } = deps.solarScrub;
  const { tileWorker } = pyramidView;
  const { dtmGroup } = deps.plotMount;
  const { syncMarks, applySelection } = deps.selection;
  const { applySun } = deps.plotSun;
  const {
    orthoMeanOf, orthoLowPassOf, inheritedGroundColour, inheritedGroundColourMap,
  } = deps.groundColour;
  const { noReleaseClear, surveyRings } = descentLoad;

  /**
   * `--ship --inject survey-recompute`: the SURFACE build takes its own survey
   * again instead of the one the claim already scored.
   */
  let surveyRecompute = TEST_SWITCHES && urlFlag('surveyrecompute');
  /**
   * `--ship --inject no-build-budget`: the SURFACE build's own steps run back
   * to back on one frame again.
   *
   * What is still on the main thread once the lattice, the uploads, the survey
   * and the scatter are staged: the orthoimage's mean and its low pass, the
   * inherited ground colour, the plot's construction and the survey handover.
   * With this on they run in one stretch, which is the frame `plotBuildBudget`
   * divides.
   */
  let buildBudgetOff = TEST_SWITCHES && urlFlag('nobuildbudget');
  /** `plot-colour-check --inject tint-early`: the build reads no colour, as
   *  when it finishes before any leaf under the anchor has one. */
  const plotTintEarly = TEST_SWITCHES && urlFlag('plottintearly');
  /** What the last SURFACE build spent where, for `--ship`'s plot-build bar. */
  let plotBuildSpans = {
    measureMs: 0, rocksMs: 0, surveyWorkMs: 0, scoreWorkMs: 0,
    surveyedHere: 0, handedSurvey: false,
    // The orthoimage's own mean, as the banded box down read it.
    orthoMean: null as number | null,
    // The budgeted steps: the longest stretch the build held the thread for,
    // the step it was holding it in, how many frames the steps spanned, and
    // each step's own arithmetic.
    stepWorstMs: 0, stepWorstName: '', stepFrames: 0,
    steps: [] as [string, number][],
  };
  /** `--ship --inject plot-mainthread`: the plot mesh built the way it was
   *  before round 10 - lattice on this thread, no staging, the link and every
   *  upload on the frame the mesh mounts. */
  let plotBuildMainThread = false;
  /**
   * `surface-clamp --band --inject plot-early-reveal`: the handover put back
   * where it was, at the end of the texture bands.
   *
   * Off `?plotearlyreveal=1` rather than a call into the page, because the
   * mount is decided the moment the SURFACE edge is crossed and a switch
   * thrown from outside would arm after the frames it is about. With it on,
   * the mesh is revealed and the camera and the fade hand over as soon as the
   * survey is sliced, with the scatter still empty and its fill back on one
   * slice to a frame around `uCamLocal` - the eye three hundred metres up.
   * The near ground is then a lattice with no rocks on it for as long as the
   * fill and its refill take, which is the shape the band arm caught.
   */
  const plotEarlyReveal = typeof location !== 'undefined' && TEST_SWITCHES
    && new URLSearchParams(location.search).get('plotearlyreveal') === '1';
  /** Whether a staged build's handover waited for the scatter. The control's
   *  readback: an inject that armed too late cannot read as engaged. */
  let plotRevealedWithRocks = false;
  /** The staged build's own mesh between the frame it joins the scene and the
   *  frame it becomes `plot`. `releasePlot` gives a half-built one back. */
  let plotBuilding: DtmPlot | null = null;
  /** What `followPlotGroundColour` needs to colour a plot after its build. */
  let plotGround: {
    plot: DtmPlot; latRad: number; lonRad: number;
    /** The level the plot's colour came from, -1 before it has one. */
    level: number;
    /** The deepest leaf there is drawn but its colour was not readable yet. */
    owed: boolean;
    /** Wall time an owed read has waited since it last retried, seconds. */
    owedWaitS: number;
    ortho: THREE.Texture | null; orthoMean: number | null; hasLowPass: boolean;
    widthM: number; heightM: number;
  } | null = null;
  /** Bumped whenever a plot is given back, so an in-flight staged build stops
   *  rather than handing the camera to ground the page has left. */
  let plotBuildGen = 0;

  /**
   * Subtrees waiting for their programs, drained inside the Atlas's own draw.
   *
   * `renderer.compileAsync` links every material it is handed and, where
   * `KHR_parallel_shader_compile` is present, waits for the driver off the main
   * thread. What it must NOT do is run outside `stage.begin()`: see the drain.
   */
  let warmQueue: Array<{ obj: THREE.Object3D; done: () => void }> = [];

  /**
   * Link this scene's programs, off the frame, and settle when they are ready.
   *
   * A stage that never becomes drawable would never drain the queue, so the
   * wait has a deadline and the caller carries on: the link then lands on the
   * frame the mesh mounts, which is where it always used to.
   */
  function warmPrograms(obj: THREE.Object3D, deadlineMs = 4000): Promise<void> {
    return new Promise<void>((resolve) => {
      let settled = false;
      const done = (): void => { if (!settled) { settled = true; resolve(); } };
      warmQueue.push({ obj, done });
      deps.live.needsRender = true;
      setTimeout(done, deadlineMs);
    });
  }

  /** Give the frame back and take it again: what a staged build yields with. */
  const nextFrame = (): Promise<void> => new Promise<void>((r) => {
    requestAnimationFrame(() => r());
  });

  /**
   * A time budget for a build that must not own a frame.
   *
   * The returned call is a no-op until `budgetMs` of wall clock has gone by
   * inside the current slice, then hands the frame back. 8 ms leaves the rest
   * of a 16.7 ms frame to the app, so a staged build costs frames rather than
   * a stall.
   */
  function frameSlices(budgetMs = 8): () => Promise<void> {
    let t0 = performance.now();
    return async (): Promise<void> => {
      if (performance.now() - t0 < budgetMs) return;
      await nextFrame();
      t0 = performance.now();
    };
  }

  /** The slice a staged build may hold a frame for; see `frameSlices`. */
  const PLOT_BUILD_SLICE_MS = 8;
  /**
   * The same budget for the build's own steps, with what each one cost.
   *
   * `step` hands the frame back BEFORE a step rather than after it, so a step
   * never starts on a frame the build has already spent; `band` is the same
   * gate for a loop inside one. What the reading is is the longest stretch the
   * build held the thread without giving a frame back, because that is the
   * frame a player feels rather than the build's total, which is meant to be
   * spread. `off` runs every step back to back and still measures them, which
   * is what makes the control readable.
   */
  function plotBuildBudget(budgetMs: number, off: boolean): {
    step: <T>(name: string, run: () => T) => Promise<T>;
    band: (name: string) => Promise<void>;
    read: () => { worstMs: number; worstName: string; frames: number; steps: [string, number][] };
  } {
    let held = performance.now();
    let mark = held;
    let frames = 1;
    let worstMs = 0;
    let worstName = '';
    const work = new Map<string, number>();
    const charge = (name: string): void => {
      work.set(name, (work.get(name) ?? 0) + (performance.now() - mark));
      const stretch = performance.now() - held;
      if (stretch > worstMs) { worstMs = stretch; worstName = name; }
    };
    const give = async (): Promise<void> => {
      if (off || performance.now() - held < budgetMs) return;
      await nextFrame();
      frames += 1;
      held = performance.now();
    };
    return {
      step: async <T>(name: string, run: () => T): Promise<T> => {
        await give();
        mark = performance.now();
        const out = run();
        charge(name);
        mark = performance.now();
        return out;
      },
      band: async (name: string): Promise<void> => {
        charge(name);
        await give();
        mark = performance.now();
      },
      read: () => ({
        worstMs: +worstMs.toFixed(1),
        worstName,
        frames,
        steps: [...work].map(([n, ms]) => [n, +ms.toFixed(1)] as [string, number]),
      }),
    };
  }

  /**
   * The whole-file plot's mesh, built once the walkaround needs it.
   *
   * Its own sub-plot list replaces the field's, because the shader draws the
   * sub-cell lattice from the same radii `buildSubPlots` used and the selection
   * is carried by identity.
   *
   * Built across frames, not in one. Round 10 measured the single frame this
   * used to be at 300 ms on the SURFACE edge: 112 ms of it the three plot
   * materials linking, 41 ms the survey, 36 ms the rock field's first fill,
   * 23 ms the 23.6 MB height texture and 11 ms the lattice. So the lattice goes
   * to the tile worker, the link is asked for asynchronously before the mesh is
   * in the scene, the textures upload one to a frame, and the survey is sliced.
   * The walkaround holds at the existing waiting state throughout - nothing is
   * mounted until the whole mesh is there.
   */
  async function ensurePlotMesh(): Promise<void> {
    const field = deps.live.plotField;
    if (deps.live.plot || deps.live.plotMeshLoading || !field || !descentLoad.state.claimUrl) return;
    const url = descentLoad.state.claimUrl;
    deps.live.plotMeshLoading = true;
    const buildT0 = performance.now();
    loadStarted();
    // The build now spans the rock fill as well, so it outlives more of what
    // can happen to the claim under it. `releasePlot` bumps the generation and
    // disposes the half-built mesh; every await here checks it is still the
    // build the page wants.
    const gen = plotBuildGen;
    const live = (): boolean => !deps.live.disposed && gen === plotBuildGen;
    // The steps still on this thread, behind the same slice the scatter's fill
    // takes. The pre-round-10 control keeps its one-frame shape.
    const budgeted = !buildBudgetOff && !plotBuildMainThread;
    const budget = plotBuildBudget(PLOT_BUILD_SLICE_MS, !budgeted);
    plotBuildSpans = {
      measureMs: 0, rocksMs: 0, surveyWorkMs: 0, scoreWorkMs: 0,
      surveyedHere: 0, handedSurvey: false, orthoMean: null,
      stepWorstMs: 0, stepWorstName: '', stepFrames: 0, steps: [],
    };
    try {
      let albedo: THREE.Texture | null = null;
      if (field.meta.albedo_texture) {
        // Decoded off the main thread for the reason src/atlas/imageBitmap.ts
        // records: a TextureLoader image defers the decode into the one
        // indivisible upload, and this orthoimage read 33.9 ms there on the
        // frame the mesh mounts. Unflipped, since the plot samples the file's
        // own rows.
        //
        // `loadPlotField` already started this fetch alongside the claim, so
        // the round trip is spent while the descent is still above the edge;
        // the fallback is only for a field this build did not come from there.
        const bitmap = await (field.albedoPromise
          ?? loadBitmap(`${url}/${field.meta.albedo_texture.file}`));
        // Before any other await, so a `releasePlot` racing this sees the
        // flag either already true or settles the promise itself, never both.
        field.albedoConsumed = true;
        albedo = new THREE.Texture(bitmap);
        albedo.needsUpdate = true;
        markLoad();
        // A plot that finishes loading after the tab closed would build itself
        // into a scene nobody owns any more.
        if (deps.live.disposed) { albedo.dispose(); bitmap.close(); return; }
      }
      // The same texture under a const, so the budgeted steps below can close
      // over it, and one place that gives it back: a texture dispose does not
      // release the decode's bitmap, and from the first yield below nothing can
      // assume the page still wants this build.
      const ortho = albedo;
      const dropOrtho = (): void => {
        if (ortho?.image instanceof ImageBitmap) ortho.image.close();
        ortho?.dispose();
      };
      const row = rowById(state.bodyId);
      if (!row) throw new Error(`no body row for "${state.bodyId}"`);
      // Not the soft read behind `bodyClimate`: a descent onto a body whose
      // season is unknown is scored against a season nobody fitted. The
      // exception is a plot built from the leaves, where refusing the season
      // would refuse the walkaround itself - sixteen of the nineteen siteless
      // bodies have no fitted season - so it stands on the same
      // mean-distance, zero-declination substitute the sky takes and the card
      // says which fields it stood in for.
      const pyramidField = isPyramidPlot(field.meta);
      const soft = pyramidField ? softClimate(row, rowById) : null;
      const climate = soft ? soft.climate : plotClimate(row);
      // The plot draws the same ground the leaves under it do, so it takes
      // their colour: the tint is what they inherit and the orthoimage is
      // luminance detail over it, exactly as `createTileMesh` reads it.
      // `DtmPlot` levels the hue against its own curve rather than taking the
      // albedo outright, for the reason its `tint` option records. The mean is
      // RAW because that shader samples the ortho with no transfer on it.
      const anchor = deps.descentAnchorHandle.regionAnchorLatLon();
      const siteId = site()?.id;
      // This image's own mean, with the site index's as the fallback. Summed a
      // band of rows at a time: the boxed-down image is a megapixel and its
      // readback and sum was the longest single thing left on this frame.
      const orthoMean = ortho
        ? (await orthoMeanOf(ortho, budgeted ? budget.band : null))
          ?? (siteId ? pyramidView.siteAlbedo?.meanRawFor(siteId) ?? null : null)
        : null;
      if (!live()) { dropOrtho(); return; }
      // Read back on both sides of the control: the banded box down has to
      // give the mean the single blit gives, or the budget has bought a
      // differently shaded plot.
      plotBuildSpans.orthoMean = orthoMean;
      // Both or neither: shading the colour by `ortho / mean` needs the mean the
      // ratio is against, and a wrong one is a level of its own. A field with
      // no orthoimage at all is the third case: the shader reads a flat 0.5
      // there, so the ratio is 1 and the tint stands alone - which is what
      // keeps a plot built from the leaves the colour of the leaves it
      // replaces rather than the ramp's own middle.
      const inherited = anchor && (ortho ? orthoMean !== null : true) && !plotTintEarly
        ? await budget.step('ground-colour',
          () => inheritedGroundColour((anchor.lat * Math.PI) / 180, (anchor.lon * Math.PI) / 180))
        : null;
      const tint = inherited?.rgb ?? null;
      if (!live()) { dropOrtho(); return; }
      // The lattice, off the main thread. `plotLatticeSpec` is what both sides
      // derive the rings and the origin from, so the buffers the worker builds
      // and the ones the fallback would are the same lattice; the control and a
      // dead worker take the same fallback.
      const spec = plotLatticeSpec(field);
      const w = plotBuildMainThread ? null : tileWorker();
      let lattice: LatticeBuffers | null = null;
      if (w?.live) {
        try {
          lattice = await w.buildLattice({
            radiusM: spec.radiusM, rings: spec.rings, places: spec.places, origin: spec.origin,
          });
        } catch {
          lattice = null;   // a worker that died mid-build is the fallback's job
        }
        if (deps.live.disposed) { dropOrtho(); return; }
      }
      if (!live()) { dropOrtho(); return; }
      // What the leaves divide by, over the same ground. Built only where the
      // tint branch will sample it, since nothing else would dispose it; the
      // mean stays as the fallback and as where `mars-curve` reads its curve.
      const orthoLowPass = ortho && tint && orthoMean !== null
        ? await budget.step('ortho-lowpass',
          () => orthoLowPassOf(ortho, field.meta.bbox_m.width, field.meta.bbox_m.height))
        : null;
      if (!live()) { dropOrtho(); orthoLowPass?.dispose(); return; }
      const p = await budget.step('construct', () => new DtmPlot(field, ortho, renderer, {
        ...(lattice ? { lattice } : {}),
        exaggeration: solar.exag,
        atmo: plotAtmoRef(),
        shadow: cascades.plotShadowRef(),
        albedoRamp: globeMaterial.albedoRampFor(row),
        palette: globeMaterial.plotPaletteFor(row),
        // The equatorial radius, not the volumetric mean: the ground the shader
        // curves is the tile pyramid's, and the pyramid is addressed on the
        // ellipsoid (tools/dtm/quadkey.py).
        curveRadiusM: row.ellipsoid.a_m,
        climate,
        bedforms: row.atmosphere.kind !== 'no_atmosphere',
        rockLaw: deps.inspector.rockLawFor(row),
        ...(tint && inherited ? { tint, tintLevel: inherited.level } : {}),
        ...(tint && orthoMean !== null ? { orthoMean, orthoLowPass } : {}),
      }));
      // Handed over before the next yield rather than at the scene join below:
      // the constructor's own buffers and both orthoimage textures are the
      // plot's from here, so a landing given back on any frame between this one
      // and the mount has to find them on `plotBuilding`.
      if (!live()) { p.dispose(); return; }
      plotBuilding = p;
      plotGround = anchor && (ortho ? orthoMean !== null : true)
        ? {
          plot: p, latRad: (anchor.lat * Math.PI) / 180, lonRad: (anchor.lon * Math.PI) / 180,
          level: tint && inherited ? inherited.level : -1, owed: tint === null, owedWaitS: 0,
          ortho, orthoMean, hasLowPass: orthoLowPass !== null,
          widthM: field.meta.bbox_m.width, heightM: field.meta.bbox_m.height,
        }
        : null;
      if (tint) {
        const map = await inheritedGroundColourMap(
          field.meta.bbox_m.width, field.meta.bbox_m.height, tint, budgeted ? budget.band : null);
        if (live()) p.setColourMap(map);
        else map?.dispose();
      }
      // The scatter is 40 ms of fill and its own programs, and it builds on the
      // first update - which `setRockTuning` is. Held until the mesh is up.
      if (!plotBuildMainThread) p.holdRocks(true);
      // `step` gives the frame back BEFORE it runs the callback, so liveness
      // is checked inside it and not only after the await: a `releasePlot`
      // during that yield has already disposed `p`.
      await budget.step('rock-tuning', () => {
        if (!live()) return;
        p.setRockTuning({ ...deps.rockTuningPanel.rockTuning });
        p.setClaimCircum(deps.injects.state.claimCircumDebug);
      });
      if (!live()) return;
      let subs: SubPlot[];
      if (plotBuildMainThread) {
        subs = p.measure(surveyRings(field), solar.lsDeg);
      } else {
        // The link first: ANGLE defers it and the info-log read at first draw
        // is what blocks, so asking for it here spends the wait on frames the
        // mesh is not in yet. A driver that refuses leaves the link where it
        // was, on the mount frame.
        await nextFrame();
        if (!live()) return;
        dtmGroup.add(p.group);
        // Hidden until the whole mesh is there, its scatter included: the
        // ground the pyramid was already drawing stays in its place, which is
        // what a person saw the notch before.
        p.group.visible = false;
        cascades.armCascades();
        // One mesh to a frame: the driver does the link work on this thread
        // even where `KHR_parallel_shader_compile` is advertised, so the whole
        // group at once is one 120 ms frame and one mesh at a time is three
        // short ones. Since round 10e the mount has linked all three, so this
        // finds them cached and settles on the next frame; it is the fallback
        // for a mount that did not run, which is what `link-in-leg` puts back.
        for (const child of [...p.group.children]) await warmPrograms(child);
        // From here the mesh is `plotBuilding`'s, so an abandoned build leaves
        // it to `releasePlot` rather than removing and disposing it twice.
        if (!live()) return;
        // Then the uploads, in row bands across frames. One texture to a
        // frame was not enough: the 23.6 MB height field and its mip chain are
        // ONE `texSubImage2D` per level, 38.9 ms on the frame that took them,
        // which is most of what the PLOT to SURFACE handoff cost.
        for (const tex of p.textures) {
          await nextFrame();
          if (!live()) return;
          await uploadTextureStaged(renderer, tex, nextFrame, live);
          if (!live()) return;
        }
        await nextFrame();
        if (!live()) return;
        // As late as the build can leave it. `loadClaim` publishes `plotSubs`
        // only once every cell is scored and `releasePlot` empties it, so a
        // non-empty array here is the survey of the ground this build is
        // about - and a claim that was still slicing when the mesh was
        // constructed has had the lattice, the warm and the texture bands to
        // finish in.
        if (!surveyRecompute && !descentLoad.state.plotLoading && deps.live.plotSubs.length > 0) {
          plotBuildSpans.handedSurvey = await budget.step('adopt-survey',
            () => (live() ? p.adoptSurvey(deps.live.plotSubs) : false));
          if (!live()) return;
        }
        const measureT0 = performance.now();
        subs = await p.measureStaged(surveyRings(field), solar.lsDeg, nextFrame);
        plotBuildSpans.measureMs = performance.now() - measureT0;
        plotBuildSpans.surveyWorkMs = p.surveyWorkMs;
        plotBuildSpans.scoreWorkMs = p.scoreWorkMs;
        plotBuildSpans.surveyedHere = p.surveyedHere;
        if (!live()) return;
      }
      markLoad();
      dtmGroup.add(p.group);
      // A control armed before the mesh landed still has to reach it.
      p.setCellMode(deps.injects.state.cellsModeStuck ? 'fill' : deps.plotControls.state.cellMode);
      if (deps.cameraRig.handoverMarsTint) p.setPalette(globeMaterial.plotPaletteFor(deps.bodyScene.state.bodyRows.find((r) => r.id === 'mars')));
      if (deps.farField.edgeMarsCurve) {
        p.setTintCurve(globeMaterial.albedoRampFor(deps.bodyScene.state.bodyRows.find((r) => r.id === 'mars')) ?? null);
      }
      if (deps.cameraRig.handoverStepM !== 0) p.liftMeshM(deps.cameraRig.handoverStepM);
      /**
       * The whole handover, on one frame.
       *
       * Three things used to ride `plot = p`: the mesh being drawn, the camera
       * leaving the world frame for the walkaround's rig, and the `descent`
       * fade retiring the orrery and the pyramid with it. Any one of them alone
       * leaves the eye over ground nothing is drawing - hold only the mesh and
       * six notches draw neither, gate only the camera and the focus rig solves
       * a pose against orbit state already in plot units, which is the black
       * frame `--frames` knows. So they go together, on the frame the last
       * texture band and the last rock cell are in.
       */
      const handover = (): void => {
        deps.live.plot = p;
        plotBuilding = null;
        deps.live.plotSubs = subs;
        if (deps.live.selectedSub) deps.live.selectedSub = subs.find((s) => s.id === deps.live.selectedSub!.id) ?? null;
        applySelection();
        deps.siteGround.refreshClaimOverlay();
        // The rig was entered on a stand-in extent, because the mesh that
        // carries the real one is what just landed.
        if (deps.cameraRig.surfaceRegime()) deps.cameraRig.enterPlotRegime();
        // The eye the walkaround holds on this very frame, handed over before
        // the mesh is drawn from it: the scatter's draw floor is a range to the
        // ground, so taking it from the descent's own hundred-odd metres would
        // coarsen the near field for the frame that reveals it.
        if (deps.cameraRig.plotCamera()) camera.position.set(0, 0, 0);
        p.update(0, deps.cameraRig.camPos, camera, deps.farField.edgeRockFloorOff ? 0 : Math.max(1, root.clientHeight));
        p.group.visible = true;
        plotRevealedWithRocks = !plotEarlyReveal;
        deps.live.needsRender = true;
        track('walkaround_start', { body_id: row.id, site_slug: siteId ?? 'none', build_ms: Math.round(performance.now() - buildT0) });
      };
      // The variant puts the old order back: revealed and handed over at the
      // end of the texture bands, with the scatter still empty and filling
      // around the eye rather than around the ground it is aimed at.
      if (plotBuildMainThread || plotEarlyReveal) handover();
      if (!plotBuildMainThread) {
        await nextFrame();
        await nextFrame();
        if (!live()) return;
        // The scatter's own first fill, one band to a frame, around the ground
        // the descent is aimed at rather than around `uCamLocal`: nothing has
        // called `update` yet, because nothing hands this plot a camera until
        // the reveal below.
        const a = deps.descentAnchorHandle.standLatLon();
        const centre = a ? deps.plotGeometry.regionXZAt(a.lat, a.lon) : null;
        // Real per-frame yield either way, so at most FILL_SLICE_INSTANCES
        // rocks land in any one frame the player sees - the dojo's
        // fill-budget reading gates on that guarantee. `plotEarlyReveal`'s
        // branch still differs only in centre: it hands over before
        // `uCamLocal` is the ground the descent aimed at, so it fills around
        // the eye instead, and the near field gets refilled once the eye
        // gets there.
        const rocksT0 = performance.now();
        if (plotEarlyReveal) await p.buildRocksStaged(nextFrame, live);
        else await p.buildRocksStaged(nextFrame, live, centre ?? undefined);
        plotBuildSpans.rocksMs = performance.now() - rocksT0;
        if (!live()) return;
        p.holdRocks(false);
        // `warmPlotPrograms` carries the three surface materials and no rock
        // species, and a subtree that has never drawn has no program linked,
        // so the reveal frame would link them all. Asked for here they land on
        // frames the mesh is not in yet.
        if (p.rocks) await warmPrograms(p.rocks.group);
        if (!live()) return;
        if (!plotEarlyReveal) handover();
      }
    } catch (e) {
      descentLoad.state.plotError = e instanceof Error ? e.message : String(e);
      track('exception', { kind: 'descent', detail: descentLoad.state.plotError, body_id: state.bodyId ?? 'none', fatal: false });
    } finally {
      deps.live.plotMeshLoading = false;
      const spent = budget.read();
      plotBuildSpans.stepWorstMs = spent.worstMs;
      plotBuildSpans.stepWorstName = spent.worstName;
      plotBuildSpans.stepFrames = spent.frames;
      plotBuildSpans.steps = spent.steps;
      loadFinished();
      if (deps.live.disposed) return;
      applySun();
      deps.overlayView.renderOverlay();
    }
  }

  /** The whole-file plot, given back: a body change or a dispose. */
  function releasePlot(): void {
    // The rover panel otherwise carries the departed plot's deployed state
    // (and JUMP TO ROVER with it) onto whatever plot loads next.
    if (deps.roverDrive.state.driving) void deps.roverDrive.setDriving(false);
    if (deps.live.plot) {
      dtmGroup.remove(deps.live.plot.group);
      deps.live.plot.dispose();
      deps.live.plot = null;
    }
    // A staged build hands its mesh to `plot` only on the reveal frame, so
    // between the mount and that frame this is the only handle on it: without
    // this a wheel back up mid-build left a whole plot in the scene graph and
    // its buffers on the card.
    plotBuildGen += 1;
    if (plotBuilding) {
      dtmGroup.remove(plotBuilding.group);
      plotBuilding.dispose();
      plotBuilding = null;
    }
    plotRevealedWithRocks = false;
    // The one place `plotField` is dropped. Its bitmap fetch started at the
    // claim (40 km out, `loadPlotField`) and most descents never reach
    // SURFACE, where `ensurePlotMesh` is the only reader that consumes it -
    // so a claim released or replaced first leaves a decoded ImageBitmap
    // (20-34 MB of RGBA8 across the baked plots on disk, against a 1.2-2.3 MB
    // encoded PNG) for GC to find with no `.close()` ever called.
    // `ensurePlotMesh` marks `albedoConsumed` before any other await once it
    // takes the bitmap, so this only ever closes one nobody owns.
    if (deps.live.plotField && !deps.live.plotField.albedoConsumed) {
      const releasedField = deps.live.plotField;
      void releasedField.albedoPromise
        ?.then((bitmap) => { if (!releasedField.albedoConsumed) bitmap.close(); })
        .catch(() => {});
    }
    deps.live.plotField = null;
    deps.live.plotSubs = [];
    descentLoad.state.pyramidPlotBody = null;
    descentLoad.state.pyramidPlotLevel = null;
    descentLoad.state.pyramidPlotHoles = 0;
    descentLoad.state.pyramidPlotFor = null;
    descentLoad.state.pyramidPlotRetryMs = 0;
    descentLoad.state.pyramidPlotNextTryMs = 0;
    descentLoad.state.claimUrl = null;
    descentLoad.state.claimGen += 1;
    // Otherwise a re-ask for this site reads as a duplicate of the ask this
    // release just doomed, and silently no-ops instead of fetching afresh.
    if (!noReleaseClear) descentLoad.state.claimWantUrl = null;
    deps.live.selectedSub = null;
    deps.live.hoverSub = null;
    syncMarks();
  }

  return {
    warmPrograms,
    frameSlices,
    ensurePlotMesh,
    releasePlot,
    plotEarlyReveal,
    state: {
      get warmQueue() { return warmQueue; },
      set warmQueue(v) { warmQueue = v; },
      get plotBuilding() { return plotBuilding; },
      get plotGround() { return plotGround; },
      set plotGround(v) { plotGround = v; },
      get plotBuildMainThread() { return plotBuildMainThread; },
      set plotBuildMainThread(v) { plotBuildMainThread = v; },
      get plotRevealedWithRocks() { return plotRevealedWithRocks; },
      get buildBudgetOff() { return buildBudgetOff; },
      set buildBudgetOff(v) { buildBudgetOff = v; },
      get surveyRecompute() { return surveyRecompute; },
      set surveyRecompute(v) { surveyRecompute = v; },
      get plotBuildSpans() { return plotBuildSpans; },
    },
  };
}
